import { GeofenceReportMode } from '../../report.models';
import { toEnglishDigits } from '../../report.utils';
import { GeofenceColumn, GeofenceReportConfig, GeofenceReportRow } from './geofence-report.models';

export const GEOFENCE_PAGE_SIZE = 20;
export const GEOFENCE_PAGE_SIZE_OPTIONS = [10, 20, 50];

const PRESENCE_COLUMNS: GeofenceColumn[] = [
  { field: 'strFullName', title: 'راننده' },
  { field: 'strMobile', title: 'موبایل' },
  { field: 'strVehicleNo', title: 'پلاک', format: 'plate' },
  { field: 'strMobileGroup_strComment', title: 'گروه' },
  { field: 'strMobileType_strComment', title: 'نوع خودرو' },
  { field: 'strMobileDomain_strComment', title: 'حوزه خدمت' },
  { field: 'strDate', title: 'تاریخ آخرین لاگ' },
  { field: 'strTime', title: 'ساعت آخرین لاگ' },
  { field: 'strDeviceStatus_strComment', title: 'وضعیت' },
];

const ENTRY_EXIT_COLUMNS: GeofenceColumn[] = [
  { field: 'strTitle', title: 'نام محدوده' },
  { field: 'strDate', title: 'تاریخ' },
  { field: 'strPresenceTime', title: 'ساعت ورود' },
  { field: 'strExitTime', title: 'ساعت خروج' },
  { field: 'strTimeInGeoFance', title: 'مدت حضور' },
];

const CONFIGS: Record<GeofenceReportMode, GeofenceReportConfig> = {
  presence: { mode: 'presence', columns: PRESENCE_COLUMNS, rowAction: 'location' },
  'last-presence': {
    mode: 'last-presence',
    columns: PRESENCE_COLUMNS.filter((column) => column.field !== 'strDeviceStatus_strComment').map((column) =>
      column.field === 'strDate' ? { ...column, title: 'تاریخ آخرین حضور' } : column.field === 'strTime' ? { ...column, title: 'ساعت آخرین حضور' } : column,
    ),
    rowAction: 'location',
  },
  'predefined-entry-exit': { mode: 'predefined-entry-exit', columns: ENTRY_EXIT_COLUMNS, rowAction: 'entry-exit-map' },
  'polygon-entry-exit': { mode: 'polygon-entry-exit', columns: ENTRY_EXIT_COLUMNS, rowAction: 'entry-exit-map' },
  'area-analysis': {
    mode: 'area-analysis',
    columns: [
      { field: 'strTitle', title: 'نام محدوده' },
      { field: 'strFullName', title: 'نام راننده' },
      { field: 'strPersonnelID', title: 'کد پرسنلی' },
      { field: 'strVehicleNo', title: 'پلاک', format: 'plate' },
      { field: 'strDate', title: 'تاریخ' },
      { field: 'strDayName', title: 'روز' },
      { field: 'strStartTime', title: 'ساعت ورود' },
      { field: 'strEndTime', title: 'ساعت خروج' },
      { field: 'strPresentTime', title: 'مدت حضور', total: 'minutes', totalField: 'siPresentTime' },
      { field: 'strMoveTime', title: 'مدت حرکت', total: 'minutes', totalField: 'siMoveTime' },
      { field: 'strStopTime', title: 'مدت توقف', total: 'minutes', totalField: 'siStopTime' },
      { field: 'fKmTotal', title: 'پیمایش (km)', format: 'number', total: 'number', unit: 'کیلومتر' },
    ],
  },
  'inspection-summary': {
    mode: 'inspection-summary',
    columns: [
      { field: 'strTitle', title: 'نام محدوده' },
      { field: 'strStatus', title: 'وضعیت بازرسی', format: 'status' },
      { field: 'strFullName', title: 'راننده' },
      { field: 'strVehicleNo', title: 'پلاک', format: 'plate' },
      { field: 'strMobileDomain_strComment', title: 'حوزه خدمت' },
      { field: 'iCount', title: 'تعداد بازرسی', format: 'number', total: 'number', unit: 'مورد' },
      { field: 'strPresenceTime', title: 'مدت کل بازرسی' },
    ],
  },
  'inspection-detail': {
    mode: 'inspection-detail',
    columns: [
      { field: 'strUnitID', title: 'عملیات', format: 'actions' },
      { field: 'strGeoFenceName', title: 'مبدأ', tone: 'origin' },
      { field: 'strFullEnterTime', title: 'ورود', tone: 'origin' },
      { field: 'strFullExitTime', title: 'خروج', tone: 'origin' },
      { field: 'strPresenceTime', title: 'حضور', tone: 'origin' },
      { field: 'strNexFeoFenceName', title: 'مقصد', tone: 'destination' },
      { field: 'strFullEnterNextTime', title: 'ورود به مقصد', tone: 'destination' },
      { field: 'strTime', title: 'مدت سیر', total: 'seconds', totalField: 'iTime' },
      { field: 'fNavigationKM', title: 'پیمایش', format: 'number', total: 'number', unit: 'متر' },
      { field: 'iAverageKM', title: 'سرعت متوسط', format: 'number' },
      { field: 'iStops', title: 'تعداد توقف', format: 'number', total: 'number', unit: 'مورد' },
      { field: 'strStopTime', title: 'مدت توقف', total: 'seconds', totalField: 'iStopsTime' },
    ],
  },
};

export function getGeofenceConfig(mode: GeofenceReportMode): GeofenceReportConfig {
  return CONFIGS[mode];
}

export function buildGeofenceSearchIndex(row: GeofenceReportRow): string {
  return toEnglishDigits(Object.entries(row).filter(([key]) => key !== 'searchIndex').map(([, value]) => value ?? '').join(' ')).toLowerCase();
}

export function sumGeofenceField(rows: GeofenceReportRow[], field: keyof GeofenceReportRow): number {
  return rows.reduce((total, row) => total + (Number(row[field]) || 0), 0);
}

export function formatMinutes(value: number): string {
  const minutes = Math.max(0, Math.floor(value || 0));
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

export function formatSeconds(value: number): string {
  const seconds = Math.max(0, Math.floor(value || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}
